"use client";

import { ChevronDown } from "lucide-react";
import { useEffect, useRef } from "react";

import type { TagDefinition } from "../../../_types/content";
import { controlStyles } from "./style-config";

function titleCase(value: string) {
  return value
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function getGroupedTagOptions(
  usedTagIds: ReadonlySet<string>,
  tags: readonly TagDefinition[],
) {
  const tagsByType = new Map<string, TagDefinition[]>();

  tags.forEach((tag) => {
    if (!usedTagIds.has(tag.id)) return;
    tagsByType.set(tag.type, [...(tagsByType.get(tag.type) ?? []), tag]);
  });

  return [...tagsByType.entries()]
    .map(([type, options]) => ({
      type,
      label: titleCase(type),
      options: options.toSorted((first, second) =>
        first.label.localeCompare(second.label),
      ),
    }))
    .toSorted((first, second) => first.label.localeCompare(second.label));
}

export function FilterDropdown({
  label,
  options,
  selectedValues,
  onToggle,
}: {
  label: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  selectedValues: readonly string[];
  onToggle: (value: string) => void;
}) {
  const dropdownRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    function closeWhenOutside(event: PointerEvent | FocusEvent) {
      const dropdown = dropdownRef.current;
      if (dropdown && !dropdown.contains(event.target as Node)) {
        dropdown.removeAttribute("open");
      }
    }

    document.addEventListener("pointerdown", closeWhenOutside);
    document.addEventListener("focusin", closeWhenOutside);

    return () => {
      document.removeEventListener("pointerdown", closeWhenOutside);
      document.removeEventListener("focusin", closeWhenOutside);
    };
  }, []);

  return (
    <details
      ref={dropdownRef}
      className="group relative min-w-44 border border-burgundy/35 bg-sand open:z-20"
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-3 font-[700] text-burgundy marker:content-none">
        <span>
          {label}
          {selectedValues.length > 0 ? ` (${selectedValues.length})` : ""}
        </span>
        <ChevronDown
          className="size-4 transition-transform group-open:rotate-180"
          aria-hidden="true"
        />
      </summary>
      <fieldset className="absolute top-full left-0 min-w-full border border-burgundy/35 bg-sand px-4 py-3 shadow-lg">
        <legend className="sr-only">{label}</legend>
        <div className="grid gap-2">
          {options.map((option) => (
            <label
              key={option.value}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm text-earth"
            >
              <input
                type="checkbox"
                checked={selectedValues.includes(option.value)}
                onChange={() => onToggle(option.value)}
                className={controlStyles.checkbox}
              />
              {option.label}
            </label>
          ))}
        </div>
      </fieldset>
    </details>
  );
}